import { ChangeEvent, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { MonthlyExpenseBranchRow, MonthlyExpenseGroup } from "../../types/monthly-expense.type";
import ConfirmModal from "../ConfirmModal";
import { EXTRA_DESCRIPTION_MAX_LENGTH, EXTRA_GROUP_OPTIONS } from "./constants";
import { ExtraForm, ExtraFormErrors, ExtraInput, extraFormErrorsOf, parseMoneyText } from "./monthlyExpenseDraft";

const BRANCH_GRID_COLUMNS = 3;
const BRANCH_LIST_MAX_HEIGHT = 180;

/** The branches and group the dialog opens with (one branch from its row, or none). */
export interface ExtraPreset {
  branchCodes: string[];
  group: MonthlyExpenseGroup;
}

interface AddExtraDialogProps {
  open: boolean;
  branches: MonthlyExpenseBranchRow[];
  preset: ExtraPreset;
  monthTitle: string;
  estimateTitle: string;
  onClose: () => void;
  onSubmit: (input: ExtraInput) => void;
}

const isExtraGroup = (value: string): value is MonthlyExpenseGroup =>
  EXTRA_GROUP_OPTIONS.some((option) => option.value === value);

/**
 * + เพิ่มค่าใช้จ่าย: one extra on one or many branches — group, a required description and the
 * month's amount per branch (> 0, ≤ 2 dp). It counts in M's actual and the M+1 estimate alike.
 * Mount it anew (a key) for each opening, so it starts from its preset.
 */
export default function AddExtraDialog({
  open,
  branches,
  preset,
  monthTitle,
  estimateTitle,
  onClose,
  onSubmit,
}: Readonly<AddExtraDialogProps>) {
  const [form, setForm] = useState<ExtraForm>({ ...preset, description: "", amountText: "" });
  const [errors, setErrors] = useState<ExtraFormErrors>({});
  const isAllPicked = form.branchCodes.length === branches.length;

  const handleAllChange = (event: ChangeEvent<HTMLInputElement>) =>
    setForm((previous) => ({
      ...previous,
      branchCodes: event.target.checked ? branches.map((branch) => branch.branch_code) : [],
    }));
  const handleBranchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setForm((previous) => ({
      ...previous,
      branchCodes: checked
        ? [...previous.branchCodes, name]
        : previous.branchCodes.filter((code) => code !== name),
    }));
  };
  const handleGroupChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (isExtraGroup(value)) setForm((previous) => ({ ...previous, group: value }));
  };
  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) =>
    setForm((previous) => ({ ...previous, description: event.target.value }));
  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) =>
    setForm((previous) => ({ ...previous, amountText: event.target.value }));

  const handleSubmit = () => {
    const nextErrors = extraFormErrorsOf(form);
    setErrors(nextErrors);
    const parsed = parseMoneyText(form.amountText);
    if (Object.keys(nextErrors).length > 0 || "message" in parsed || parsed.amount === null) return;
    onSubmit({ branchCodes: form.branchCodes, group: form.group, description: form.description, amount: parsed.amount });
  };

  return (
    <ConfirmModal
      open={open}
      title="เพิ่มค่าใช้จ่ายเพิ่มเติม"
      confirmLabel="เพิ่มรายการ"
      showCloseButton
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Stack gap={2}>
        <Typography variant="body2" color="text.secondary">
          ค่าใช้จ่ายที่เกิดขึ้นจริงในเดือน{monthTitle}แต่ไม่อยู่ใน 16 รายการ นับรวมทั้งยอดจริงของเดือน{monthTitle}และ{estimateTitle}
        </Typography>
        <Stack>
          <Typography variant="body2" color="text.secondary">
            สาขา *
          </Typography>
          <Box
            sx={{
              border: 1,
              borderColor: errors.branchCodes ? "error.main" : "divider",
              borderRadius: 2,
              px: 1,
              maxHeight: BRANCH_LIST_MAX_HEIGHT,
              overflow: "auto",
              display: "grid",
              gridTemplateColumns: `repeat(${BRANCH_GRID_COLUMNS}, 1fr)`,
            }}
          >
            <FormControlLabel
              sx={{ gridColumn: "1 / -1" }}
              control={<Checkbox size="small" checked={isAllPicked} onChange={handleAllChange} />}
              label={`ทั้งหมด (${branches.length} สาขา)`}
            />
            {branches.map((branch) => (
              <FormControlLabel
                key={branch.branch_code}
                control={
                  <Checkbox
                    size="small"
                    name={branch.branch_code}
                    checked={form.branchCodes.includes(branch.branch_code)}
                    onChange={handleBranchChange}
                  />
                }
                label={branch.branch_code}
              />
            ))}
          </Box>
          {errors.branchCodes && <FormHelperText error>{errors.branchCodes}</FormHelperText>}
        </Stack>
        <TextField select size="small" label="กลุ่ม *" value={form.group} onChange={handleGroupChange}>
          {EXTRA_GROUP_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="คำอธิบาย *"
          placeholder="เช่น ค่าซ่อมแอร์สำนักงาน"
          value={form.description}
          onChange={handleDescriptionChange}
          error={Boolean(errors.description)}
          helperText={errors.description}
          slotProps={{ htmlInput: { maxLength: EXTRA_DESCRIPTION_MAX_LENGTH } }}
        />
        <TextField
          size="small"
          label="ยอดเดือน ต่อสาขา (บาท) *"
          value={form.amountText}
          onChange={handleAmountChange}
          error={Boolean(errors.amountText)}
          helperText={errors.amountText}
          slotProps={{ htmlInput: { inputMode: "decimal", style: { textAlign: "right" } } }}
        />
      </Stack>
    </ConfirmModal>
  );
}
